/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { selectIdToken } from "../storage";

/**
 * Invalidates the cached permissions whenever the persisted ID token changes.
 *
 * `usePermissionsQuery` keys on the account number and the B2C account id, and holds the token in
 * its `queryFn` closure instead — so React Query cannot see a renewed token on its own, and a
 * permissions result fetched with a stale token survives until its one-day `gcTime` expires. A
 * cached *error* is the damaging case: it is what leaves the navigation stripped down until the
 * user reloads. Invalidating is what turns the renewed token into a fresh fetch.
 *
 * The refetch carries the new token because `usePermission` reads the same Redux value and
 * re-renders before this effect runs, so the query observer already holds a `queryFn` closing over
 * the renewed token by the time the invalidation reaches it.
 *
 * `["permissions"]` is a prefix and React Query matches partially, so one call covers every
 * account's entry — a token change invalidates them all, not just the account on screen.
 *
 * The ref is seeded with the token present on the first render so that mounting does not
 * invalidate: at that point nothing has changed, and refetching would only duplicate the request
 * `usePermission` is already making.
 */
export const useRefreshPermissionsOnTokenChange = () => {
  const idToken = useSelector(selectIdToken);
  const queryClient = useQueryClient();
  const previousIdToken = useRef(idToken);

  useEffect(() => {
    if (previousIdToken.current === idToken) {
      return;
    }

    previousIdToken.current = idToken;
    queryClient.invalidateQueries({ queryKey: ["permissions"] });
  }, [idToken, queryClient]);
};
